// src/pages/ProjectDetails.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { formatDate } from './helpers';
import SeoHelmet from '../../common/SeoHelmet';
import ProjectDetailsNameCard from './components/ProjectDetailsNameCard';
import ProjectDetailsDetailsCard from './components/ProjectDetailsDetailsCard';
import ProjectDetailsNotesCard from './components/ProjectDetailsNotesCard';
import ProjectDetailsDangerZone from './components/ProjectDetailsDangerZone';
import ProjectDetailsCalculationsCard from './components/ProjectDetailsCalculationsCard';
import { Alert, Button, Box, Skeleton, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const statusMeta = {
  in_progress: { chipColor: 'primary', variant: 'outlined' },
  finished: { chipColor: 'success', variant: 'outlined' },
  archived: { chipColor: 'default', variant: 'outlined' },
};

const ProjectDetails = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [project, setProject] = useState(null);

  const created = formatDate(project?.createdAt);
  const updated = formatDate(project?.updatedAt);

  useEffect(() => {
    let mounted = true;

    const fetchProject = async () => {
      if (!projectId) {
        setError('Missing project id.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const res = await axios.get(`/api/v1/project/${projectId}`);
        const doc = res?.data?.data?.project || res?.data?.data?.doc || res?.data?.data || res?.data || null;

        if (!mounted) return;

        if (!doc) {
          setProject(null);
          setError('Project not found.');
        } else {
          setProject(doc);
        }
      } catch (err) {
        if (!mounted) return;
        const msg = err?.response?.data?.message || err?.message || 'Failed to load project.';
        setError(msg);
        setProject(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchProject();

    return () => {
      mounted = false;
    };
  }, [projectId]);

  const status = project?.status;
  const meta = useMemo(() => statusMeta[status] || { chipColor: 'default', variant: 'outlined' }, [status]);

  return (
    <>
      <SeoHelmet title="GeoCalc | Project Details" indexable={false} />
      <Header />
      <div className="project-details-container">
        {/* Top bar */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')}>
            Back to Projects
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rounded" animation="wave" height={60} sx={{ borderRadius: 2, mb: 5 }} />
          </Box>
        ) : (
          <ProjectDetailsNameCard
            projectId={projectId}
            project={project}
            meta={meta}
            status={status}
            created={created}
            updated={updated}
            onProjectUpdated={setProject}
          />
        )}
        {loading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rounded" animation="wave" height={60} sx={{ borderRadius: 2, mb: 5 }} />
          </Box>
        ) : (
          <ProjectDetailsDetailsCard
            projectId={projectId}
            project={project}
            meta={meta}
            status={status}
            created={created}
            updated={updated}
            onProjectUpdated={setProject}
          />
        )}
        {loading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rounded" animation="wave" height={220} sx={{ borderRadius: 2, mb: 5 }} />
          </Box>
        ) : (
          <ProjectDetailsNotesCard projectId={projectId} project={project} onProjectUpdated={setProject} />
        )}

        {loading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rounded" animation="wave" height={220} sx={{ borderRadius: 2, mb: 5 }} />
          </Box>
        ) : (
          <ProjectDetailsCalculationsCard projectId={projectId} project={project} onProjectUpdated={setProject} />
        )}

        {loading ? (
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="rounded" animation="wave" height={60} sx={{ borderRadius: 2, mb: 5 }} />
          </Box>
        ) : (
          <ProjectDetailsDangerZone projectId={projectId} />
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </div>

      <Footer />
    </>
  );
};

export default ProjectDetails;
